import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Profile.css";

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogoutSuccess, setShowLogoutSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const storedUserData = localStorage.getItem("userData");

    if (!token || !storedUserData) {
      navigate("/login");
      return;
    }

    // Check if token is expired
    const tokenExpires = localStorage.getItem("tokenExpires");
    if (tokenExpires && Date.now() > parseInt(tokenExpires)) {
      localStorage.clear();
      navigate("/login");
      return;
    }

    try {
      const user = JSON.parse(storedUserData);
      setUserData(user);
    } catch (error) {
      console.error("Error parsing user data:", error);
      navigate("/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleLogout = () => {
    // Show beautiful logout success modal
    setShowLogoutSuccess(true);
    
    // Clear localStorage and redirect after 2 seconds
    setTimeout(() => {
      localStorage.clear();
      navigate("/login");
    }, 2000);
  };

  const closeLogoutModal = () => {
    setShowLogoutSuccess(false);
    localStorage.clear();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading-spinner">Loading Profile...</div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="profile-container">
        <div className="error-message">No user data found. Please login again.</div>
      </div>
    );
  }

  // Extract first and last name from full_name
  const nameParts = userData.full_name?.split(' ') || ['', ''];
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return (
    <div className="profile-container">
      {/* Logout Success Modal */}
      {showLogoutSuccess && (
        <div className="logout-modal-overlay">
          <div className="logout-modal">
            <div className="logout-icon">👋</div>
            <h3>Logged Out Successfully</h3>
            <p>You have been securely logged out of your account.</p>
            <div className="logout-details">
              <div className="logout-item">
                <span className="logout-label">Session Ended:</span>
                <span className="logout-value">{new Date().toLocaleTimeString()}</span>
              </div>
              <div className="logout-item">
                <span className="logout-label">Member ID:</span>
                <span className="logout-value">{userData.membership_id}</span>
              </div>
            </div>
            <div className="logout-progress">
              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>
              <span>Redirecting to login page...</span>
            </div>
            <button className="logout-close-btn" onClick={closeLogoutModal}>
              Go to Login Now
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="profile-header">
        <h2>Profile & ID Card</h2>
      </div>

      {/* Membership Status */}
      <div className="membership-status card">
        <div className="status-left">
          <img src="/assets/user.png" alt="profile" className="profile-img" />
          <div>
            <h3 className="profile-name">
              {userData.full_name} <span className="volunteer-badge">{userData.membership_category}</span>
            </h3>
          </div>
        </div>
        <div className="status-right">
          <p>
            <strong>Member ID:</strong> {userData.membership_id}
          </p>
          <p>
            <strong>Membership Since:</strong> {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
          <span className={`status-badge ${userData.status.toLowerCase()}`}>
            {userData.status} Member
          </span>
        </div>
      </div>

      {/* Personal Details */}
      <div className="card">
        <div className="section-header">
          <h4>Personal Details</h4>
        </div>
        <form className="form-grid">
          <div>
            <label>First Name</label>
            <input 
              type="text" 
              value={firstName} 
              disabled={!isEditing} 
            />
          </div>
          <div>
            <label>Last Name</label>
            <input 
              type="text" 
              value={lastName} 
              disabled={!isEditing} 
            />
          </div>
          <div>
            <label>Email</label>
            <input
              type="email"
              value={userData.email}
              disabled={!isEditing}
            />
          </div>
          <div>
            <label>Phone Number</label>
            <input
              type="text"
              placeholder="08045123652"
              disabled={!isEditing}
            />
          </div>
          <div>
            <label>City</label>
            <input 
              type="text" 
              value={userData.geographic_info?.state || ''} 
              disabled={!isEditing} 
            />
          </div>
          <div className="full-width">
            <label>Address</label>
            <input
              type="text"
              placeholder="Full address will appear here"
              disabled={!isEditing}
            />
          </div>
        </form>
      </div>

      {/* Political Info */}
      <div className="card">
        <h4>Political Information</h4>
        <div className="political-info-grid">
          <div>
            <label>State</label>
            <input 
              type="text" 
              value={userData.geographic_info?.state || ''} 
              disabled={!isEditing} 
            />
          </div>
          <div>
            <label>Senatorial District</label>
            <input 
              type="text" 
              value={userData.geographic_info?.sd || ''} 
              disabled={!isEditing} 
            />
          </div>
          <div>
            <label>Federal Constituency</label>
            <input 
              type="text" 
              value={userData.geographic_info?.fc || ''} 
              disabled={!isEditing} 
            />
          </div>
          <div>
            <label>Local Government</label>
            <input 
              type="text" 
              value={userData.geographic_info?.lg || ''} 
              disabled={!isEditing} 
            />
          </div>
          <div>
            <label>Ward</label>
            <input 
              type="text" 
              value={userData.geographic_info?.ward || ''} 
              disabled={!isEditing} 
            />
          </div>
          <div>
            <label>Polling Unit</label>
            <input 
              type="text" 
              value={userData.geographic_info?.pu || ''} 
              disabled={!isEditing} 
            />
          </div>
        </div>
      </div>

      {/* Membership Card */}
      <div className="card membership-card">
        <div className="id-card">
          <div className="id-left">
            <p className="id-title">MEMBER ID CARD</p>
            <p className="id-subtitle">Political Association</p>
            <h3 className="id-name">{userData.full_name}</h3>
            <p className="id-member-id">{userData.membership_id}</p>
            <div className="id-details">
              <span>{userData.geographic_info?.ward || 'Ward'}</span>
              <span>{userData.membership_category}</span>
            </div>
          </div>
          <div className="id-right">
            <img src="/assets/user.png" alt="profile" className="profile-img-small" />
            <p className="member-since">Member Since {new Date().getFullYear()}</p>
            <p className="member-status">{userData.status}</p>
          </div>
        </div>
        
        <div className="card-actions">
          <button className="edit-btn" onClick={handleEditToggle}>
            {isEditing ? "Save Changes" : "Edit Profile"}
          </button>
          {/* <button className="download-btn">
            ⬇ Download Membership Card
          </button> */}
        </div>
      </div>

      {/* Logout Button - Now at the very bottom of the page */}
      <div className="logout-container">
        <button className="logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </div>
    </div>
  );
};

export default Profile;